/**
 * TenantBrandingProvider: contexto que fetcha y expone el branding del tenant
 * según el slug del URL. Si no hay slug en el URL, branding queda null.
 *
 * Aplica el color primario como CSS variable `--color-primary` en el root.
 */
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { type TenantBranding, getTenantBranding } from "../api/tenant_branding";
import { ZONA_POR_DEFECTO } from "../lib/fecha";

interface BrandingContextValue {
  branding: TenantBranding | null;
  isLoading: boolean;
  isError: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  isLoading: false,
  isError: false,
});

const DEFAULT_PRIMARY = "#1D4ED8"; // azul por defecto

export function TenantBrandingProvider({ children }: { children: ReactNode }) {


  const { data, isLoading, isError } = useQuery({
    queryKey: ["branding"],
    queryFn: getTenantBranding,
    staleTime: 5 * 60_000, // branding cambia raramente
    retry: false,
  });

  const branding = (data as TenantBranding | undefined) ?? null;
  const primaryColor = branding?.color_primario || DEFAULT_PRIMARY;
  // Sin segundo color, el logotipo se escribe todo en el primario: mejor un
  // nombre monocromo que uno con un rojo inventado por nosotros.
  const secondaryColor = branding?.color_secundario || primaryColor;

  // EL NOMBRE EN LA PESTAÑA.
  //
  // El index.html trae un título neutro porque se sirve igual en cualquier
  // instalación; el nombre de verdad solo se sabe cuando responde el servidor.
  // Sin esto, la pestaña decía "MOTOR POLICLÍNICO" —el nombre interno del
  // producto— en la pantalla que ve el personal del policlínico todos los
  // días.
  useEffect(() => {
    const nombre = branding?.nombre_comercial;
    if (nombre) document.title = nombre;
  }, [branding?.nombre_comercial]);

  // Aplica los colores como CSS vars para que cualquier componente los use
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--color-primary", primaryColor);
    root.style.setProperty("--color-secondary", secondaryColor);
    return () => {
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-secondary");
    };
  }, [primaryColor, secondaryColor]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, isError }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useTenantBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

/**
 * Zona horaria del policlínico para formatear instantes.
 *
 * Mientras el branding carga, o en pantallas sin tenant en el URL, cae a
 * America/Lima. Nunca devuelve la zona del navegador: una hora de consulta
 * pintada en la zona de quien mira es una hora equivocada.
 */
export function useZonaHoraria(): string {
  const { branding } = useTenantBranding();
  return branding?.zona_horaria || ZONA_POR_DEFECTO;
}

/**
 * Prefijo de las rutas del policlínico: ninguno.
 *
 * Se queda como función —y no se borra— porque la usan cuarenta y pico sitios
 * para armar enlaces. Devolver "" es lo correcto ahora que todo cuelga de la
 * raíz, y deja el enganche puesto si algún día vuelve a hacer falta.
 */
export function useBase(): string {
  return "";
}
